export type WorkflowStageStatus = "pending" | "in_progress" | "approved" | "changes_requested";

export type WorkflowStage = {
  id: number;
  position: number;
  name: string;
  status: WorkflowStageStatus;
  assignee_id: number | null;
  assignee_email: string | null;
  decided_by_email: string | null;
  decided_at: string | null;
  note: string | null;
};

export type WorkflowEvent = {
  id: number;
  event_type: string;
  actor_email: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type SubmissionWorkflow = {
  started: boolean;
  active: boolean;
  can_manage: boolean;
  stages: WorkflowStage[];
  events: WorkflowEvent[];
};

export type WorkflowSequenceStage = { name: string; assignee_id: number | null };

export type WorkflowSequence = {
  id: number;
  name: string;
  stages: WorkflowSequenceStage[];
  use_count: number;
  last_used_at: string | null;
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function parseOrThrow(response: Response) {
  if (response.ok) return response.status === 204 ? null : response.json();
  const data = await response.json().catch(() => ({}));
  throw new Error(data.errors?.join(", ") ?? data.error ?? `Request failed with ${response.status}`);
}

function jsonHeaders() {
  return { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() };
}

function workflowBase(municipalityId: number, submissionId: number): string {
  return `/api/v1/municipalities/${municipalityId}/submissions/${submissionId}/workflow`;
}

export async function getWorkflow(municipalityId: number, submissionId: number): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(await fetch(workflowBase(municipalityId, submissionId)));
  return data.workflow;
}

export async function startWorkflow(
  municipalityId: number,
  submissionId: number,
  stages: WorkflowSequenceStage[],
  sequenceId?: number | null,
): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(
    await fetch(`${workflowBase(municipalityId, submissionId)}/start`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ stages, sequence_id: sequenceId ?? null }),
    }),
  );
  return data.workflow;
}

export async function updateWorkflowStages(
  municipalityId: number,
  submissionId: number,
  stages: WorkflowSequenceStage[],
): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(
    await fetch(`${workflowBase(municipalityId, submissionId)}/stages`, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ stages }),
    }),
  );
  return data.workflow;
}

async function decideStage(
  municipalityId: number,
  submissionId: number,
  stageId: number,
  action: "approve" | "request_changes",
  note?: string,
): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(
    await fetch(`${workflowBase(municipalityId, submissionId)}/stages/${stageId}/${action}`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ note: note ?? null }),
    }),
  );
  return data.workflow;
}

export async function approveStage(
  municipalityId: number,
  submissionId: number,
  stageId: number,
  note?: string,
): Promise<SubmissionWorkflow> {
  return decideStage(municipalityId, submissionId, stageId, "approve", note);
}

export async function requestStageChanges(
  municipalityId: number,
  submissionId: number,
  stageId: number,
  note?: string,
): Promise<SubmissionWorkflow> {
  return decideStage(municipalityId, submissionId, stageId, "request_changes", note);
}

export async function resumeWorkflow(municipalityId: number, submissionId: number): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(
    await fetch(`${workflowBase(municipalityId, submissionId)}/resume`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
    }),
  );
  return data.workflow;
}

export async function cancelWorkflow(municipalityId: number, submissionId: number): Promise<SubmissionWorkflow> {
  const data = await parseOrThrow(
    await fetch(`${workflowBase(municipalityId, submissionId)}/cancel`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
    }),
  );
  return data.workflow;
}

export type WorkflowMember = { id: number; email: string };

export type WorkflowSequencesResponse = {
  sequences: WorkflowSequence[];
  members: WorkflowMember[];
};

export async function listWorkflowSequences(municipalityId: number): Promise<WorkflowSequencesResponse> {
  return parseOrThrow(await fetch(`/api/v1/municipalities/${municipalityId}/workflow_sequences`));
}

export async function createWorkflowSequence(
  municipalityId: number,
  name: string,
  stages: WorkflowSequenceStage[],
): Promise<WorkflowSequence> {
  const data = await parseOrThrow(
    await fetch(`/api/v1/municipalities/${municipalityId}/workflow_sequences`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ name, stages }),
    }),
  );
  return data.sequence;
}

export async function renameWorkflowSequence(
  municipalityId: number,
  sequenceId: number,
  name: string,
): Promise<WorkflowSequence> {
  const data = await parseOrThrow(
    await fetch(`/api/v1/municipalities/${municipalityId}/workflow_sequences/${sequenceId}`, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ name }),
    }),
  );
  return data.sequence;
}

export async function deleteWorkflowSequence(municipalityId: number, sequenceId: number): Promise<void> {
  await parseOrThrow(
    await fetch(`/api/v1/municipalities/${municipalityId}/workflow_sequences/${sequenceId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() },
    }),
  );
}
